/**
 * Copyright 2015-present Singlepoint. All Rights Reserved.
 *
 * @flow
 */

import '../../styles/commonPageStyles.scss';
import './ViewHomeMultipleQuotesPage.scss';

import { TitleWithUnderLine } from '@boi/core/lib';
import React, { useEffect, useState } from 'react';
import type { Match, RouterHistory } from 'react-router-dom';

import AnimatedSpinner from '../../components/AnimatedSpinner/AnimatedSpinner';
import GenericErrorComponent from '../../components/GenericErrorComponent';
import { MobileBackButton } from '../../components/MobileBackButton';
import { PropertyMapper } from '../../components/PremiumBreakdowns/HomePremiumBreakdown/AmountMapper';
import ProgressBar from '../../components/ProgressBar';
import QuoteOverviewContainer from '../../components/QuoteOverviewContainer/QuoteOverviewContainer';
import QuoteRenewalOverviewContainer
  from '../../components/QuoteRenewalOverviewContainer/QuoteRenewalOverviewContainer';
import {
  commonPageStylesIdentifier,
  HOME_QUOTE_STORAGE,
  SELECTED_HOME_QUOTE_STORAGE,
  TOGGLE_OPTIONAL_EXTRAS
} from '../../constants';
import { REDIRECT_BACK } from '../../constants/commonConstants';
import { policyImportantDocuments } from '../../constants/externalLinks';
import { INSURER_TYPE, QUOTE_REFERENCE } from '../../constants/home';
import { ABOUT_YOU, CODE_VALUES, premiumBreakDownConstants } from '../../constants/insuranceTypeConstants';
import {
  HOME_JOURNEY,
  JOURNEY_GET_QUOTE,
  JOURNEY_YOUR_QUOTE,
  RENEWAL_JOURNEY_LABEL_TEXT,
  STAGES_HOME_JOURNEY,
  STAGES_RENEWAL_JOURNEY
} from '../../constants/journey';
import { localPhoneNumber } from '../../constants/phoneNumbers';
import { LAST_PAGE_NAME, POLICY_RENEWAL } from '../../constants/sessionStorage/genericStorageIdentifiers';
import {
  AGENT_SUMMARY_QUOTE_STORAGE, HOME_CUSTOMER_GIVEN_NAME,
  RENEWAL_DIRECT_DEBIT_POLICY
} from '../../constants/sessionStorage/homeStorageIdentifiers';
import { CommonPageStylesDesktopBackButton } from '../../constants/styleConstants/commonClassNames';
import {
  getObjectFromSessionStorage,
  removeFromSessionStorage, saveInLocalStorage,
  saveInSessionStorage,
  saveObjectInSessionStorage
} from '../../helpers';
import { routes } from '../../routes';
import { getRenewalQuote } from '../../services/homeServices';
import type { Insurer } from '../../types';
import type {
  DefaultFinalQuote,
  FinalQuote,
  FinalQuoteRenewal,
  HomePayloadType,
  HomeQuoteStorage,
  PremiumDetail,
  RenewalQuoteResponseModel
} from '../../types/Home';
import { documentLink } from '../../utils/utils';

const ipidLabel = () => (
  <>
    Important Documents {documentLink(policyImportantDocuments, 'Click here')}
  </>
);

const benefits = [
  { label: () => 'Pay by direct debit at no extra cost ', tick: true },
  { label: () => '24 hour helpline in a home emergency ', tick: true },
  { label: () => 'Loss of keys/lock replacement', tick: true },
  { label: () => 'Fire brigade charges, up to €2,500', tick: true },
  { label: () => ipidLabel(), tick: true }
];

const renewalBenefits = [
  {
    label: () => 'Please review policy documents for full details',
    tick: true
  },
  {
    label: () => 'Product coverage is comparable across our insurer panel',
    tick: true
  },
  { label: () => 'Pay by Direct Debit at no extra cost', tick: true },
  { label: () => '24 hour helpline in home emergency', tick: true }
];

type DefaultFinalQuoteMapping = {
  RSA: DefaultFinalQuote,
  FBD: DefaultFinalQuote
};

const defaultQuoteObj: DefaultFinalQuoteMapping = {
  RSA: {
    logo: 'asset/rsa_logo.jpg',
    underwrittenClass: 'intactLogo',
    price: 0,
    INSURER_TYPE: INSURER_TYPE.RSA
  },
  FBD: {
    logo: 'asset/fbd_logo.jpg',
    underwrittenClass: 'fbdLogo',
    price: 0,
    INSURER_TYPE: INSURER_TYPE.FBD
  }
};

type Props = {
  history: RouterHistory,
  match: Match
};

// eslint-disable-next-line complexity
const ViewHomeMultipleQuotesPage = ({ history, match }: Props) => {
  const className = 'c-ViewMultipleQuotePage';
  useEffect(() => {
    if (getObjectFromSessionStorage(SELECTED_HOME_QUOTE_STORAGE)) {
      removeFromSessionStorage(`${SELECTED_HOME_QUOTE_STORAGE}`);
    }
    saveInSessionStorage(REDIRECT_BACK, true);
  }, []);

  const [quotesFinal, setQuotes] = useState<FinalQuote[]>([]);
  // $FlowFixMe Initialized to empty object for some reason
  const [renewalQuotes, setRenewalQuotes] = useState<FinalQuoteRenewal>({});
  const [renewalError, setRenewalError] = useState<boolean>(false);
  const [showMultipleQuote, setShowMultipleQuote] = useState(true);
  const [insurerSpecified, setInsurerSpecified] = useState( 'UNSPECIFIED');

  useEffect(() => {
    saveInLocalStorage("INSURER_SPECIFIED",insurerSpecified);
  }, [insurerSpecified]);

  const updateInsurerSpecified = (newValue: string) => {
    setInsurerSpecified(newValue);
    saveInLocalStorage("INSURER_SPECIFIED", newValue);
  };

  const renewalPolicy = getObjectFromSessionStorage(POLICY_RENEWAL);

  const lastPage = getObjectFromSessionStorage(LAST_PAGE_NAME);

  const isEditMode = lastPage && lastPage === 'Summary';

  function defaultQuoteDisplay() {
    const fbdQuote: DefaultFinalQuote = {
      ...defaultQuoteObj[INSURER_TYPE.FBD],
      price: 0
    };
    const rsaQuote: DefaultFinalQuote = {
      ...defaultQuoteObj[INSURER_TYPE.RSA],
      price: 0
    };
    // $FlowFixMe expected FinalQuote[]
    setQuotes([fbdQuote, rsaQuote]);
  }

  function commitQuoteReferenceToSessionStorage(quote_reference?: string | null) {
    if (quote_reference && quote_reference.trim().length > 0) {
      saveObjectInSessionStorage(
        QUOTE_REFERENCE,
        quote_reference
      );
    }
  }

// eslint-disable-next-line complexity
  const getQuoteResponse = async () => {
    const isHomeQuote = getObjectFromSessionStorage(HOME_QUOTE_STORAGE);
    const agentSummaryQuote = getObjectFromSessionStorage(`${AGENT_SUMMARY_QUOTE_STORAGE}`);

    function isHomeQuoteOrRenewal(): boolean {
      return (isHomeQuote && isHomeQuote?.response?.length > 0) || renewalPolicy?.renewal === true;
    }

    if (isHomeQuoteOrRenewal()) {
      if (renewalPolicy.renewal && Object.keys(isHomeQuote).length === 0) {
        const data = await getRenewalQuote(
          { policy_code: renewalPolicy.reference_number },
          match,
          { history }
        );
        if (!data.error) {
          saveObjectInSessionStorage(HOME_QUOTE_STORAGE, { response: data });
        } else {
          setRenewalError(true);
        }
      }
      const homeQuote: HomeQuoteStorage = getObjectFromSessionStorage(
        HOME_QUOTE_STORAGE
      );
      if (homeQuote.response && homeQuote.response.length > 0) {
        commitQuoteReferenceToSessionStorage(homeQuote?.response[0]?.quote_reference);
        setQuoteDetailsFromResponse(homeQuote.response);
      } else {
        defaultQuoteDisplay();
      }
    } else if (agentSummaryQuote) {
      if (agentSummaryQuote.length > 0) {
        commitQuoteReferenceToSessionStorage(agentSummaryQuote[0]?.quote_reference);
        setQuoteDetailsFromResponse(agentSummaryQuote);
        const policyHolderName = agentSummaryQuote[0]?.quote_policy_holder_details?.first_name ? agentSummaryQuote[0]?.quote_policy_holder_details?.first_name : '';
        saveInSessionStorage(HOME_CUSTOMER_GIVEN_NAME, policyHolderName);
      } else {
        defaultQuoteDisplay();
      }
    }
  };

  const updateQuote = (
    responseArr: (HomePayloadType | RenewalQuoteResponseModel)[]
  ): FinalQuote[] => {
    const quotesData = [];
    let quoteObj = {};
    responseArr.forEach((item: HomePayloadType | RenewalQuoteResponseModel) => {
      quoteObj = {
        ...item,
        ...defaultQuoteObj[item.insurer],
        price: getQuoteTotal(item.insurer, responseArr)
      };
      quotesData.push(quoteObj);
    });
    return quotesData;
  };

  const quoteGood = (quote: HomePayloadType | RenewalQuoteResponseModel) =>
    !quote.acceptance_notifications ||
    (quote.acceptance_notifications &&
      quote.acceptance_notifications.length === 0);

  // eslint-disable-next-line complexity
  const setQuoteDetailsFromResponse = async (
    response: (HomePayloadType | RenewalQuoteResponseModel)[]
  ) => {
    const alreadySelected = Object.keys(
      getObjectFromSessionStorage(SELECTED_HOME_QUOTE_STORAGE)
    ).length;

    const onlyOneQuoteNumber = () => {
      let goodCount = 0;
      let savedNumber = 0;

      response.forEach(
        (quote: HomePayloadType | RenewalQuoteResponseModel, index: number) => {
          if (quoteGood(quote)) {
            goodCount++;
            savedNumber = index + 1;
          }
        }
      );
      return goodCount > 1 ? 0 : savedNumber;
    };

    const oneQuoteResult = onlyOneQuoteNumber();

    if (oneQuoteResult > 0) {
      if (alreadySelected && !renewalPolicy.renewal) {
        removeFromSessionStorage(`${SELECTED_HOME_QUOTE_STORAGE}`);
        history.push(`${routes.getQuotePage.url}${ABOUT_YOU}`);
      } else {
        const oneQuoteResultAsIndex = oneQuoteResult - 1;
        if (renewalPolicy.renewal) {
          setShowMultipleQuote(false);
          // $FlowFixMe Casting Quote[] to (RenewalQuoteResponseModel & DefaultFinalQuote)[]
          const updatedQuote: (RenewalQuoteResponseModel &
            DefaultFinalQuote)[] = await updateQuote([
            response[oneQuoteResultAsIndex]
          ]);
          setRenewalQuotes(updateQuoteSequence(updatedQuote));
        } else {
          goToViewQuotePage(response[oneQuoteResultAsIndex].insurer);
        }
      }
    } else {
      const updatedQuotes = await updateQuote(response);
      if (updatedQuotes.length > 0) {
        if (renewalPolicy.renewal) {
          // $FlowFixMe Casting updatedQuotes to (RenewalQuoteResponseModel & DefaultFinalQuote)[]
          setRenewalQuotes(updateQuoteSequence(updatedQuotes));
        } else {
          setQuotes(updatedQuotes);
        }
      }
    }
  };

  const updateQuoteSequence = (
    quotesData: (DefaultFinalQuote & RenewalQuoteResponseModel)[]
  ): FinalQuoteRenewal => {
    const renewalQuote = quotesData.find(
      (item: DefaultFinalQuote & RenewalQuoteResponseModel) => {
        // $FlowFixMe Flow can't infer type (RenewalQuoteResponseModel & DefaultFinalQuote)
        if (item.current_insurer_renewal_quote) {
          return item;
        }
      }
    );
    const filteredArray: (DefaultFinalQuote &
      RenewalQuoteResponseModel)[] = quotesData.filter(
      (item: DefaultFinalQuote & RenewalQuoteResponseModel) => {
        if (!item.current_insurer_renewal_quote) {
          return item;
        }
      }
    );
    return {
      currentRenewal: renewalQuote,
      alternativeQuote: filteredArray || []
    };
  };

  const getQuoteTotal = (
    quoteType: Insurer,
    response: (HomePayloadType | RenewalQuoteResponseModel)[]
  ): number => {
    const quote = response.find(
      (quote: HomePayloadType | RenewalQuoteResponseModel) =>
        quote.insurer === quoteType
    );
    if (quote?.premium_details.length) {
      // $FlowFixMe quote.premium_details does exist
      const element = quote.premium_details.find((item: PremiumDetail) => {
        if (item.code === CODE_VALUES[quoteType]) {
          return item;
        }
      });
      const propertyIdentifier =
        PropertyMapper[quoteType][premiumBreakDownConstants.INCLEVY] === 'AMT'
          ? 'amount'
          : 'running_total';
      // $FlowFixMe Assuming that element exists
      return element[propertyIdentifier] ? element[propertyIdentifier] : 0;
    } else {
      return 0;
    }
  };

  // eslint-disable-next-line flowtype/no-weak-types
  // eslint-disable-next-line complexity
  const goToViewQuotePage = (selectedQuoteType: Insurer, alternativePolicy?: any) => {
    updateInsurerSpecified(JSON.stringify("SPECIFIED"));
    const state: HomeQuoteStorage = getObjectFromSessionStorage(`${HOME_QUOTE_STORAGE}`);
    const quoteArr = getObjectFromSessionStorage(`${AGENT_SUMMARY_QUOTE_STORAGE}`);
    if (isEditMode) {
      const toggleOptionalExtras = getObjectFromSessionStorage(TOGGLE_OPTIONAL_EXTRAS);
      if (toggleOptionalExtras) {
        saveObjectInSessionStorage(TOGGLE_OPTIONAL_EXTRAS, { ...toggleOptionalExtras, insurerType: selectedQuoteType });
      }
    }
    if (state && state?.response?.length > 0) {
      setSelectedQuoteAndRedirect(state.response, selectedQuoteType, alternativePolicy);
    } else if (quoteArr && quoteArr.length > 0) {
      setSelectedQuoteAndRedirect(quoteArr, selectedQuoteType, alternativePolicy);
    }
  };

  // eslint-disable-next-line flowtype/require-parameter-type
  const setSelectedQuoteAndRedirect = (quoteArr, selectedQuoteType, alternativePolicy) => {
    const selectedInsurer = quoteArr.find(
      (quote: HomePayloadType | RenewalQuoteResponseModel) => quote.insurer === selectedQuoteType
    );
    // $FlowFixMe Probably not needed
    quoteArr.selectedQuoteType = selectedQuoteType;
    saveObjectInSessionStorage(SELECTED_HOME_QUOTE_STORAGE, {
      ...selectedInsurer,
      ...alternativePolicy
    });

    saveInSessionStorage(
      RENEWAL_DIRECT_DEBIT_POLICY,
      // $FlowFixMe direct_debit_policy only present for renewals
      selectedInsurer?.direct_debit_policy
    );

    quoteArr.filter(quoteGood).length > 1
      ? history.push({ pathname: '/wallet/viewQuote/home' })
      : history.replace({ pathname: '/wallet/viewQuote/home' });
  };

  // eslint-disable-next-line complexity
  const mobileBackButtonAndTitle = () => {
    return (
      <>
        <div
          className={`${commonPageStylesIdentifier} ${CommonPageStylesDesktopBackButton}`}
        >
          <MobileBackButton history={history} />
        </div>
        <ProgressBar
          stage={renewalPolicy.renewal ? JOURNEY_GET_QUOTE : JOURNEY_YOUR_QUOTE}
          labelText={
            renewalPolicy.renewal ? RENEWAL_JOURNEY_LABEL_TEXT : HOME_JOURNEY
          }
          stages={
            renewalPolicy.renewal ? STAGES_RENEWAL_JOURNEY : STAGES_HOME_JOURNEY
          }
        />
        {!renewalPolicy?.renewal && (
          <div className={`${commonPageStylesIdentifier}__pageTitle`}>
            <TitleWithUnderLine>Your Quotes</TitleWithUnderLine>
          </div>
        )}
      </>
    );
  };

  const showSingleQuote = () => {
    return (
      <div className={`${className}`}>
        <>
          {mobileBackButtonAndTitle()}
          {Object.keys(renewalQuotes).length < 2 && (
            <AnimatedSpinner size="large" />
          )}
          <QuoteRenewalOverviewContainer
            classes={'c-QuoteOverview'}
            goToViewQuotePage={goToViewQuotePage}
            quotesFinal={renewalQuotes}
            benefits={renewalBenefits}
          />
        </>
      </div>
    );
  };

  // eslint-disable-next-line complexity
  const showMultipleQuotes = () => {
    return (
      <div className={`${className}`}>
        <>
          {mobileBackButtonAndTitle()}
          {renewalPolicy.renewal && (
            <>
              {Object.keys(renewalQuotes)?.length < 2 && (
                <AnimatedSpinner size="large" />
              )}
              <QuoteRenewalOverviewContainer
                classes={'c-QuoteOverview'}
                goToViewQuotePage={goToViewQuotePage}
                quotesFinal={renewalQuotes}
                benefits={renewalBenefits}
              />
            </>
          )}

          {!renewalPolicy.renewal && quotesFinal.length > 0 && (
            <>
              {quotesFinal?.length === 0 && <AnimatedSpinner size="large" />}
              <QuoteOverviewContainer
                classes={className}
                goToViewQuotePage={goToViewQuotePage}
                quotesFinal={quotesFinal}
                benefits={benefits}
              />
            </>
          )}
        </>
      </div>
    );
  };

  useEffect(() => {
    if (!isEditMode) {
      removeFromSessionStorage(TOGGLE_OPTIONAL_EXTRAS);
    }
    getQuoteResponse();
  }, []);

  useEffect(() => {
    return () => {
      if (history.action === 'POP') {
        removeFromSessionStorage(TOGGLE_OPTIONAL_EXTRAS);
        removeFromSessionStorage(`${SELECTED_HOME_QUOTE_STORAGE}`);
      }
    };
  }, [history]);

  return (
    <>
      {renewalError && (
        <>
          <GenericErrorComponent title={'Your Renewal'} history={history}>
            <h5>
              Unfortunately your renewal quote cannot be displayed right now,
              please contact us for further assistance on
              <a href={`tel:${localPhoneNumber}`}> {localPhoneNumber}</a>
            </h5>
          </GenericErrorComponent>
        </>
      )}
      {!renewalError && (
        <>
          {showMultipleQuote && showMultipleQuotes()}
          {!showMultipleQuote && showSingleQuote()}
        </>
      )}
    </>
  );
};

export default ViewHomeMultipleQuotesPage;
